import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useAuth } from '@/hooks/useAuth';
import { useStudentPayments } from '@/hooks/useStudentPayments';
import { Payment, PaymentStatus } from '@/lib/types';

const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

function formatCurrency(value: number): string {
  return `R$ ${value.toFixed(2).replace('.', ',')}`;
}

function StatusCard({ status, student, payments }: {
  status: PaymentStatus;
  student: NonNullable<ReturnType<typeof useStudentPayments>['student']>;
  payments: Payment[];
}) {
  if (status === 'no_fee') return null;

  const now = new Date();
  const monthLabel = `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
  const currentPayment = payments.find(p => {
    const d = new Date(p.paid_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const configs: Record<Exclude<PaymentStatus, 'no_fee'>, { icon: string; color: string; title: string; subtitle: string }> = {
    paid: {
      icon: '✅',
      color: '#2E7D32',
      title: 'Pagamento confirmado',
      subtitle: currentPayment
        ? `${formatCurrency(currentPayment.amount)} · pago em ${new Date(currentPayment.paid_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
        : '',
    },
    pending: {
      icon: '⏳',
      color: '#F9A825',
      title: 'Pagamento pendente',
      subtitle: `Vencimento: dia ${student.due_day ?? 10} · ${formatCurrency(student.monthly_fee ?? 0)}`,
    },
    overdue: {
      icon: '⚠️',
      color: '#C8102E',
      title: 'Pagamento vencido',
      subtitle: `Venceu dia ${student.due_day ?? 10} · ${formatCurrency(student.monthly_fee ?? 0)}`,
    },
  };

  const cfg = configs[status as Exclude<PaymentStatus, 'no_fee'>];

  return (
    <View style={[styles.statusCard, { borderLeftColor: cfg.color }]}>
      <Text style={styles.statusIcon}>{cfg.icon}</Text>
      <View style={styles.statusInfo}>
        <Text style={styles.statusMonth}>{monthLabel}</Text>
        <Text style={[styles.statusTitle, { color: cfg.color }]}>{cfg.title}</Text>
        <Text style={styles.statusSubtitle}>{cfg.subtitle}</Text>
      </View>
    </View>
  );
}

function PaymentHistoryRow({ payment }: { payment: Payment }) {
  const d = new Date(payment.paid_at);
  const monthLabel = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;

  return (
    <View style={styles.historyRow}>
      <View style={styles.historyLeft}>
        <Text style={styles.historyMonth}>{monthLabel}</Text>
        <Text style={styles.historyMethod}>{payment.method}</Text>
      </View>
      <View style={styles.historyRight}>
        <Text style={styles.historyAmount}>{formatCurrency(payment.amount)}</Text>
        <Text style={styles.historyDate}>
          {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
        </Text>
      </View>
    </View>
  );
}

export default function StudentFinanceiroScreen() {
  const { profile } = useAuth();
  const { student, payments, currentMonthStatus, loading } = useStudentPayments(profile?.id ?? '');

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color="#C8102E" />
      </View>
    );
  }

  if (!student || !student.monthly_fee) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyIcon}>💳</Text>
        <Text style={styles.emptyText}>Nenhuma mensalidade configurada</Text>
        <Text style={styles.emptySubtext}>Entre em contato com seu personal trainer</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Meus Pagamentos</Text>

      <StatusCard status={currentMonthStatus} student={student} payments={payments} />

      {payments.length > 0 && (
        <>
          <Text style={styles.sectionLabel}>Histórico</Text>
          <View style={styles.historyCard}>
            {payments.map((p, i) => (
              <View key={p.id} style={[i < payments.length - 1 && styles.historyRowBorder]}>
                <PaymentHistoryRow payment={p} />
              </View>
            ))}
          </View>
        </>
      )}

      {payments.length === 0 && (
        <Text style={styles.emptyHistory}>Nenhum pagamento registrado ainda</Text>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 16, paddingBottom: 40 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  title: { fontSize: 22, fontWeight: '700', color: '#1A1A1A', marginBottom: 14 },
  statusCard: { backgroundColor: '#fff', borderRadius: 12, padding: 14, flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 20, borderLeftWidth: 4, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  statusIcon: { fontSize: 28 },
  statusInfo: { flex: 1 },
  statusMonth: { fontSize: 11, color: '#888', marginBottom: 2 },
  statusTitle: { fontSize: 15, fontWeight: '700' },
  statusSubtitle: { fontSize: 12, color: '#888', marginTop: 2 },
  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 },
  historyCard: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  historyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 14 },
  historyRowBorder: { borderBottomWidth: 1, borderBottomColor: '#f5f5f5' },
  historyLeft: {},
  historyMonth: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  historyMethod: { fontSize: 11, color: '#888', marginTop: 1, textTransform: 'capitalize' },
  historyRight: { alignItems: 'flex-end' },
  historyAmount: { fontSize: 14, fontWeight: '700', color: '#1A1A1A' },
  historyDate: { fontSize: 11, color: '#888', marginTop: 1 },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyText: { fontSize: 16, fontWeight: '600', color: '#1A1A1A', textAlign: 'center' },
  emptySubtext: { fontSize: 13, color: '#888', marginTop: 4, textAlign: 'center' },
  emptyHistory: { textAlign: 'center', color: '#888', marginTop: 20 },
});
